import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Paper, Grid, Button, Theme, Popper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { taskDetailsActions } from 'app/components/Tasks/store/Tasks.redux';
import { useSelector } from 'react-redux';
import { selectCueRecipient } from '../store/Tasks.selectors';
import { DateRangePicker } from 'app/ComponentLibrary/DateRangeNew';
import { getCueNoteFilterOptions } from './CueNoteFilterFormat';
import { CuedTo, ICueListForPlacement, RepliedTo } from 'app/models/Tasks/Tasks';
import { IPlacementDetails } from 'app/models/Placement/PlacementDetails';
import { selectPlacementDetails } from 'store/redux-store/placement-details/selectors';
import moment from 'moment';
import { TaskTreeView } from './TaskTreeView';

const useStyles = makeStyles()((theme: Theme) => ({
  actionContainer: {
    backgroundColor: '#ffffff',
    '&:hover, &:focus': {
      backgroundColor: '#ffffff',
    },
    // position: 'sticky',
    bottom: -8,
    width: '100%',
    borderTop: '1px solid #cccccc',
    padding: '5px 20px',
    zIndex: 10,
  },
  button: {
    fontSize: 14,
    textTransform: 'none',
    '&:hover, &:focus': {
      background: 'none',
    },
  },
  cancelButton: {
    color: '#888888',
  },
  countTag: {
    color: '#555555',
    fontSize: 14,
    fontWeight: 600,
  },
  paper: {
    width: '305px',
    overflow: 'hidden',
    borderRadius: '4px',
    boxShadow: '0px 2px 8px #0000004D',
  },
  childWrapper: {
    maxHeight: '300px',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '5px 0px',
  },
}));

export const CueNotesFilter = ({
  openFeedFilter,
  setFeedFilter,
  setIsFilterApplied,
  showMyCues,
  dateRangeDisplay,
  setDateRangeDisplay,
  setShowMyCues,
  isFilterApplied,
  selectedFilters,
  setSelectedFilters,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  existingFilter,
  setExistingFilters,
}: {
  openFeedFilter: boolean;
  setFeedFilter: (e) => void;
  setIsFilterApplied: (e) => void;
  showMyCues: boolean;
  dateRangeDisplay: any;
  setDateRangeDisplay: (e) => void;
  setShowMyCues: (e) => void;
  isFilterApplied: boolean;
  selectedFilters: any[];
  setSelectedFilters: (e) => void;
  startDate: any;
  setStartDate: (e) => void;
  endDate: any;
  setEndDate: (e) => void;
  existingFilter: any[];
  setExistingFilters: (e) => void;
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const boxRef = React.useRef<any>();
  const cueRecipients = useSelector(selectCueRecipient);
  const treeData = getCueNoteFilterOptions(cueRecipients);

  const [filterCount, setFilterCount] = useState<string[]>([]);
  const foundPlacement: IPlacementDetails = useSelector(selectPlacementDetails);

  const clearAll = () => {
    setStartDate(null);
    setEndDate(null);
    setDateRangeDisplay(null);
    setSelectedFilters([]);
  };

  const clearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setDateRangeDisplay(null);
  };

  const onChange = val => {
    setSelectedFilters(val);
  };

  const setDateRange = e => {
    setStartDate(moment.utc(e?.startDate).format());
    setEndDate(moment.utc(e?.endDate).format());
  };

  const cancelChanges = () => {
    setSelectedFilters(existingFilter);
    setFeedFilter(false);
  };

  const applySelectedFilters = () => {
    if ((!selectedFilters || selectedFilters?.length === 0) && startDate === null && endDate === null) {
      setIsFilterApplied(false);
    } else {
      setIsFilterApplied(true);
    }
    setExistingFilters(selectedFilters);
    const data = {
      placementId: foundPlacement?.header?.placementId,
      createdStartDate: startDate || null,
      createdEndDate: endDate || null,
      cuedTo: selectedFilters[0]?.name === CuedTo ? selectedFilters[0]?.children?.map(c => parseInt(c.value)) : null,
      repliedToId:
        selectedFilters[0]?.name === RepliedTo ? selectedFilters[0]?.children?.map(c => parseInt(c.value)) : null,
    } as ICueListForPlacement;
    if (data?.placementId > 0) {
      dispatch(taskDetailsActions.getCueNotesForPlacements(data));
    }
  };

  const formatForDateRangePicker = e => {
    const dates = {
      startDate: moment(e.startDate).format(),
      endDate: moment(e.endDate).format(),
    };
    return dates;
  };

  useEffect(() => {
    setSelectedFilters([]);
    setDateRangeDisplay(null);
    setExistingFilters([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFilterApplied === false) {
      setStartDate(null);
      setEndDate(null);
      setSelectedFilters([]);
      setDateRangeDisplay(null);
      setExistingFilters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFilterApplied]);

  useEffect(() => {
    if (showMyCues) {
      setSelectedFilters([]);
      setDateRangeDisplay(null);
    } else {
      const dates = {
        startDate: startDate,
        endDate: endDate,
      };
      setSelectedFilters(existingFilter);
      setDateRangeDisplay(dates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyCues]);

  useEffect(() => {
    const count: string[] = [];
    selectedFilters?.forEach(a => {
      count.push(...a?.children?.map(z => z.value));
    });
    setFilterCount(count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters]);

  return (
    <div>
      <React.Fragment>
        <Popper
          anchorEl={boxRef.current}
          role={undefined}
          style={{ top: '95px', left: '-20px', position: 'absolute', zIndex: 102, overflow: 'visible' }}
          transition
          disablePortal
          open={openFeedFilter}
          modifiers={{
            offset: {
              enabled: true,
            },
          }}
        >
          <Paper elevation={0} className={classes.paper}>
            <Grid
              item
              container
              justifyContent={'space-between'}
              alignItems="center"
              style={{ padding: '6px 20px', minWidth: 240 }}
            >
              <div className={classes.countTag}>{`${
                filterCount && filterCount.length > 0 ? `${filterCount?.length}` : 0
              } ${t('search.filter.Selected')}`}</div>
              <Button
                className="clear-all-button"
                aria-label="clear-all-button"
                variant="text"
                color="primary"
                disableRipple
                disableFocusRipple
                disableTouchRipple
                onClick={clearAll}
                classes={{ text: ` ${classes.button}` }}
                style={{ padding: '6px 0px' }}
              >
                {t('search.filter.clearAll')}
              </Button>
            </Grid>
            <div style={{ padding: '0px 20px' }}>
              <div>
                <DateRangePicker
                  variant="filled"
                  maxWidth={300}
                  onApply={e => setDateRange(e)}
                  onCancel={() => clearDates()}
                  onChange={e => {
                    e?.startDate && e?.endDate && setDateRangeDisplay(formatForDateRangePicker(e));
                  }}
                  initialDateRange={{
                    startDate: dateRangeDisplay?.startDate,
                    endDate: dateRangeDisplay?.endDate,
                    useLabelAsValue: true,
                  }}
                  clickAway={true}
                  disablePortal={true}
                  quickSelect={false}
                  value={dateRangeDisplay}
                  placeholder={t('notification.taskSidePanel.cueNotes.cueFilterName')}
                  fullWidth={true}
                  popperPlacement={'left'}
                />
              </div>
              <div className={classes.childWrapper}>
                <TaskTreeView
                  options={treeData}
                  onChange={onChange}
                  value={selectedFilters}
                  size={'large'}
                  defaultExpanded={['1-Cued To', '2-Replied To']}
                  isAlternateDisabled={true}
                />
              </div>
            </div>
            <div className={classes.actionContainer}>
              <Grid item container justifyContent="flex-end" alignItems="center">
                <Button
                  className="clear-all-button"
                  aria-label="apply-button"
                  variant="text"
                  color="primary"
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  onClick={() => {
                    setFeedFilter(false);

                    applySelectedFilters();
                  }}
                  classes={{ text: ` ${classes.button}` }}
                >
                  {t('search.filter.apply')}
                </Button>
                <Button
                  className="clear-all-button"
                  aria-label="cancel-button"
                  variant="text"
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  onClick={() => cancelChanges()}
                  classes={{ text: ` ${classes.button} ${classes.cancelButton}` }}
                  style={{ padding: '6px 0px', justifyContent: ' end' }}
                >
                  {t('search.filter.cancel')}
                </Button>
              </Grid>
            </div>
          </Paper>
        </Popper>
      </React.Fragment>
    </div>
  );
};
